// Storage-persistence watchdog.
//
// main.jsx has REQUESTED navigator.storage.persist() at every boot since
// 2026-06-17, but nothing ever checked the browser's answer. Jill's iPad,
// 2026-08-07, on PPU wifi (no internet): iOS evicted the portal's entire
// origin storage in one sweep — the service worker's precached shell (Safari's
// "FetchEvent.respondWith … Load failed" page instead of an offline boot), the
// slip draft's unsynced edits, and the IndexedDB vault all went together. The
// persist() request was in place; whether that device ever GRANTED it is
// exactly the reading nobody took. The vault cannot help here: it lives in the
// same origin bucket the eviction removes.
//
// Two jobs, both best-effort and never load-bearing:
//
// 1. REPORT each device's protection status to seapa_debug_log — once per
//    CHANGE, not per boot (the last-reported value is kept in localStorage).
//    A useful side effect: eviction deletes the last-reported marker too, so a
//    device that was quiet for weeks suddenly re-reporting is itself the
//    fingerprint of a wipe, timestamped and attributable.
//
// 2. RETRY a denied request during a real user gesture. Browsers decide
//    persist() silently on engagement heuristics; a request made mid-tap on a
//    device that is actively using the app stands a better chance than the
//    boot-time call, which fires before any interaction.
import { supabase } from "../supabaseClient";

const LAST_REPORTED_KEY = "seapa_persist_reported_v1";

async function readStatus() {
  if (!navigator.storage?.persisted) return "unsupported";
  try {
    return (await navigator.storage.persisted()) ? "granted" : "denied";
  } catch {
    return "unreadable";
  }
}

async function readEstimate() {
  try {
    const { usage, quota } = await navigator.storage.estimate();
    const mb = (n) => `${Math.round(n / 1024 / 1024)}MB`;
    return `using ${mb(usage || 0)} of ${mb(quota || 0)}`;
  } catch {
    return "estimate unavailable";
  }
}

function report(status, detail) {
  try {
    supabase
      .from("seapa_debug_log")
      .insert({
        step: "storage_persist_status",
        msg: `${status} | ${detail} | ${navigator.userAgent}`,
      })
      .then(() => {}, () => {});
  } catch {
    /* reporting is best-effort */
  }
}

function rememberReported(status) {
  try {
    localStorage.setItem(LAST_REPORTED_KEY, status);
  } catch {
    /* a full device just reports again next boot */
  }
}

// Called once from main.jsx after render. vaultSentinelWasPresent is the
// boot-time reading of the seapa_vault_ok sentinel (taken BEFORE
// installDraftVault re-stamps it): false on a device whose localStorage did
// not survive since the previous run — corroborating evidence when it rides
// along in the report.
export async function watchStoragePersistence(vaultSentinelWasPresent) {
  const status = await readStatus();
  let lastReported = null;
  try {
    lastReported = localStorage.getItem(LAST_REPORTED_KEY);
  } catch {
    /* unreadable storage — report every boot rather than never */
  }

  if (status !== lastReported) {
    const estimate = await readEstimate();
    const survival = vaultSentinelWasPresent
      ? "prior local data present"
      : "prior local data MISSING (wiped or first run)";
    report(status, `${estimate} | ${survival}`);
    rememberReported(status);
  }

  // A denial is worth one retry per boot, from inside a real tap.
  if (status === "denied" && navigator.storage?.persist) {
    const retryOnGesture = async () => {
      try {
        if (await navigator.storage.persist()) {
          report("granted-after-gesture", await readEstimate());
          rememberReported("granted");
        }
      } catch {
        /* still denied — next boot tries again */
      }
    };
    window.addEventListener("pointerdown", retryOnGesture, { once: true });
  }
}
